// Shared logic for restoring the page a guest originally requested once they log in.
//
// Two cases are supported:
// - Same-origin deep links into this app: captured from window.location by
//   AppRouter and stored as a root-relative path (e.g. "/organizations/3/...").
// - Cross-origin deep links from the legacy Yii app (apps.zynity.com), which
//   has no access to this app's localStorage. It appends the intended
//   destination as an absolute URL in a `?redirect=` query param when it
//   sends a guest here to log in; we capture that into the same storage slot.
import { constants } from '../config/constants';

const REDIRECT_STORAGE_KEY = constants.post_login_redirect_storage_key;
const BLOCKED_PATHS = ['/', '/login', '/site/login'];

const isAllowedRedirectHost = (hostname: string) =>
  hostname === 'zynity.com' || hostname.endsWith('.zynity.com');

// Reads a `redirect` query param (expected on the /login route) and, if it's
// an absolute URL to an allowed zynity.com host, stores it for use after
// login. Returns true if a value was captured.
export const captureExternalRedirectParam = (search: string): boolean => {
  const redirectParam = new URLSearchParams(search).get('redirect');
  if (!redirectParam) return false;

  try {
    const url = new URL(redirectParam);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return false;
    if (!isAllowedRedirectHost(url.hostname)) return false;

    localStorage.setItem(REDIRECT_STORAGE_KEY, url.toString());
    return true;
  } catch {
    return false;
  }
};

export const getPostLoginRedirectTarget = (): string | null => {
  try {
    const stored = localStorage.getItem(REDIRECT_STORAGE_KEY);
    if (!stored) return null;

    if (stored.startsWith('/')) {
      return BLOCKED_PATHS.includes(stored) ? null : stored;
    }

    const url = new URL(stored);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    if (!isAllowedRedirectHost(url.hostname)) return null;
    return url.toString();
  } catch {
    return null;
  }
};

export const clearPostLoginRedirectTarget = () => {
  try {
    localStorage.removeItem(REDIRECT_STORAGE_KEY);
  } catch {
    // ignore storage errors
  }
};

export const isExternalRedirectTarget = (target: string) => /^https?:\/\//i.test(target);
